import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, orderBy, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import Table from '../../components/Table';
import BillView from '../../components/BillView';
import { getSalonStaffRoster } from '../../services/staffConfigService';
import './SalonBookings.css';

function SalonBookings() {
  const { salonData } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [staffMap, setStaffMap] = useState({});
  const [staff, setStaff] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 30;
  
  // Staff assignment modal states
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [staffLoading, setStaffLoading] = useState(false);
  
  // Bill view states
  const [showBillView, setShowBillView] = useState(false);
  const [selectedBookingForBill, setSelectedBookingForBill] = useState(null);

  const filters = [
    { key: 'all', label: 'All Bookings' },
    { key: 'upcoming', label: 'Upcoming' },
    { key: 'completed', label: 'Completed' },
    { key: 'cancelled', label: 'Cancelled' },
    { key: 'walkin', label: 'Walk-in Customers' }
  ];

  // Past booking the salon never resolved: still 'confirmed' after its date passed
  // (the nightly auto-expire job hasn't caught it yet) or already auto-marked
  // 'expired'. Mirrors the mobile dashboard's Past tab — the salon records what
  // actually happened instead of the booking silently sitting as expired.
  const isUnresolvedPastBooking = (row) => {
    const status = String(row.bookingStatus || '').toLowerCase();
    if (status === 'expired') return true;
    if (status !== 'confirmed' || row.serviceStatus !== 'not_started') return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const bookingDay = new Date(row.bookingDate);
    bookingDay.setHours(0, 0, 0, 0);
    return bookingDay < today;
  };

  // Dynamic columns based on active filter
  const getColumns = () => {
    const baseColumns = [
      {
        key: 'bookingReference',
        title: 'Booking ID',
        className: 'booking-id-col'
      },
      {
        key: 'customerName',
        title: 'Customer Name'
      },
      {
        key: 'customerPhone',
        title: 'Customer Number'
      },
      {
        key: 'serviceName',
        title: 'Service Type'
      },
      {
        key: 'bookingDateTime',
        title: 'Date/Time',
        render: (value, row) => {
          const date = new Date(row.bookingDate);
          return `${date.toLocaleDateString()} ${row.bookingTime}`;
        }
      },
      {
        key: 'bookingStatus',
        title: 'Status',
        render: (value, row) => {
          // Past confirmed bookings the nightly job hasn't flipped yet display as
          // expired — same as the mobile dashboard's Past tab
          const display = isUnresolvedPastBooking(row) && String(value).toLowerCase() === 'confirmed'
            ? 'expired'
            : value;
          return (
            <span className={`status-badge status-${String(display).toLowerCase()}`}>
              {display}
            </span>
          );
        }
      },
      {
        key: 'totalPrice',
        title: 'Price',
        render: (value, row) => {
          if (!row.gstEnabled) return `₹${value}`;
          const payable = row.grandTotal ?? value;
          return (
            <span title={`GST (${row.gstPercentage}%): ₹${row.gstAmount}`}>
              ₹{payable} <small className="gst-included-tag">(incl. GST)</small>
            </span>
          );
        }
      },
      {
        key: 'advancePaid',
        title: 'Advance',
        render: (value, row) => {
          // Advance Pay (₹11 Razorpay advance) — fields written by Cloud Functions
          // on the booking doc; mirrors the mobile app's badges.
          if (row.advancePaid !== true) return '-';
          const isCancelled = String(row.bookingStatus || '').includes('cancelled') || row.bookingStatus === 'no_show';
          const balanceSettled = row.bookingStatus === 'completed' || row.paymentStatus === 'received';
          if (isCancelled) {
            return (
              <span className="status-badge advance-badge-muted" title="Advance is non-refundable on cancellation">
                ₹{row.advanceAmount || 11} paid (non-refundable)
              </span>
            );
          }
          return (
            <span className="advance-badge-stack">
              <span className="status-badge advance-badge-paid">✓ ₹{row.advanceAmount || 11} paid</span>
              {!balanceSettled && (
                <span className="status-badge advance-badge-remaining">
                  Collect: ₹{Math.max(Number(row.remainingAmount) || 0, 0)}
                </span>
              )}
            </span>
          );
        }
      }
    ];

    // Only show "Service Done By" for completed, cancelled, walkin, and all sections
    if (activeFilter !== 'upcoming') {
      baseColumns.push({
        key: 'serviceDoneBy',
        title: 'Service Done By',
        render: (value, row) => {
          if (row.serviceStatus === 'completed' && row.staffId) {
            return staffMap[row.staffId] || 'Unknown Staff';
          }
          return '-';
        }
      });
    }

    baseColumns.push({
      key: 'offerApplied',
      title: 'Offer Applied',
      render: (value, row) => row.offerApplied != null ? 'Yes' : 'No'
    });

    // Add action column for completed bookings
    if (activeFilter === 'completed' || activeFilter === 'all' || activeFilter === 'walkin') {
      baseColumns.push({
        key: 'actions',
        title: 'Actions',
        render: (value, row) => {
          if (row.serviceStatus === 'completed') {
            return (
              <div className="action-buttons-group">
                <button
                  onClick={() => handleViewBill(row)}
                  className="view-bill-action-btn"
                  title="View Bill"
                >
                  📄 View Bill
                </button>
                <button
                  onClick={() => handleOpenStaffModal(row)}
                  className="assign-staff-action-btn"
                  title={row.staffId ? 'Reassign Staff' : 'Assign Staff'}
                >
                  {row.staffId ? '🔄' : '👤'}
                </button>
              </div>
            );
          }
          if (isUnresolvedPastBooking(row)) {
            return (
              <div className="action-buttons-group">
                <button
                  onClick={() => handleMarkExpiredCompleted(row)}
                  className="mark-completed-action-btn"
                  title="Record that the service actually happened"
                >
                  ✓ Service Completed
                </button>
                <button
                  onClick={() => handleMarkExpiredNoShow(row)}
                  className="mark-noshow-action-btn"
                  title="Record that the customer never arrived"
                >
                  ✗ Didn't Arrive
                </button>
              </div>
            );
          }
          return '-';
        }
      });
    }

    return baseColumns;
  };

  const fetchStaff = useCallback(() => {
    const roster = getSalonStaffRoster(salonData);
    const staffData = {};
    roster.forEach((staffMember) => {
      staffData[staffMember.id] = staffMember.name;
    });
    setStaffMap(staffData);
    setStaff(roster);
  }, [salonData]);

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const bookingsQuery = query(
        collection(db, 'bookings'),
        where('salonId', '==', salonData.salonId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(bookingsQuery);
      const bookingsData = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();

        // Advance Pay payment holds (payment_* statuses) are Cloud-Function-managed
        // and never shown — same filtering the mobile app applies.
        if (String(data.bookingStatus || '').startsWith('payment_')) return;

        bookingsData.push({
          id: doc.id,
          bookingId: doc.id,
          bookingReference: data.bookingReference,
          customerName: data.customerName,
          customerId: data.customerId,
          customerPhone: data.customerPhone,
          salonName: data.salonName,
          serviceName: data.serviceName,
          bookingDate: data.bookingDate,
          bookingTime: data.bookingTime,
          bookingStatus: data.bookingStatus,
          serviceStatus: data.serviceStatus,
          offerApplied: data.offerApplied,
          totalPrice: data.totalPrice,
          gstEnabled: data.gstEnabled || false,
          gstPercentage: data.gstPercentage || 0,
          gstAmount: data.gstAmount || 0,
          grandTotal: data.grandTotal,
          paymentMode: data.paymentMode,
          staffId: data.staffId,
          isWalkIn: data.isWalkIn,
          createdAt: data.createdAt,
          salonId: data.salonId,
          // Advance Pay mirror fields (written by Cloud Functions)
          advancePaid: data.advancePaid === true,
          advanceAmount: data.advanceAmount ?? null,
          remainingAmount: data.remainingAmount ?? null,
          paymentStatus: data.paymentStatus ?? null
        });
      });

      setBookings(bookingsData);
    } catch (err) {
      setError('Failed to load bookings. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [salonData?.salonId]);

  const applyFilter = useCallback(() => {
    let filtered = [...bookings];

    // Get tomorrow's date at midnight
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    switch (activeFilter) {
      case 'upcoming':
        // Only show bookings from tomorrow onwards
        filtered = bookings.filter(booking => {
          const status = booking.bookingStatus.toLowerCase();
          const isUpcomingStatus = status === 'upcoming' || status === 'confirmed' || status === 'booked';

          if (!isUpcomingStatus) return false;

          // Check if booking date is tomorrow or later
          const bookingDate = new Date(booking.bookingDate);
          const bookingDay = new Date(bookingDate);
          bookingDay.setHours(0, 0, 0, 0);

          return bookingDay >= tomorrow;
        });
        break;
      case 'completed':
        filtered = bookings.filter(booking => booking.bookingStatus.toLowerCase() === 'completed');
        break;
      case 'cancelled':
        filtered = bookings.filter(booking => {
          const status = booking.bookingStatus.toLowerCase();
          return status === 'cancelled_by_salon' || status === 'cancelled_by_customer' || status === 'cancelled';
        });
        break;
      case 'walkin':
        filtered = bookings.filter(booking => booking.isWalkIn === true);
        break;
      default:
        filtered = bookings;
    }

    setFilteredBookings(filtered);
  }, [bookings, activeFilter]);

  useEffect(() => {
    if (salonData?.salonId) {
      fetchBookings();
      fetchStaff();
    } else if (salonData && !salonData.salonId) {
      setError('Unable to load salon information. Please refresh the page.');
      setLoading(false);
    }
  }, [salonData, fetchBookings, fetchStaff]);

  useEffect(() => {
    applyFilter();
    setCurrentPage(1); // Reset to first page when filter changes
  }, [bookings, activeFilter, applyFilter]);

  const handleRefresh = () => {
    fetchBookings();
    fetchStaff();
  };

  // Staff Assignment Modal Handlers
  const handleOpenStaffModal = (booking) => {
    setSelectedBooking(booking);
    setShowStaffModal(true);
  };

  const handleCloseStaffModal = () => {
    setShowStaffModal(false);
    setSelectedBooking(null);
  };

  const handleAssignStaff = async (staffId) => {
    try {
      setStaffLoading(true);
      
      const bookingRef = doc(db, 'bookings', selectedBooking.id);
      await updateDoc(bookingRef, {
        staffId: staffId,
        updatedAt: new Date().toISOString()
      });

      alert('Staff assigned successfully!');
      handleCloseStaffModal();
      await fetchBookings(); // Refresh the bookings list
    } catch (error) {
      alert('Failed to assign staff. Please try again.');
    } finally {
      setStaffLoading(false);
    }
  };

  // Expired/unresolved past booking: salon records the service actually happened.
  // Field set mirrors the mobile app's completeServiceWithPayment. Slot counters
  // aren't touched — the booking date is already past, so availability can no
  // longer be affected.
  const handleMarkExpiredCompleted = async (row) => {
    const advanceNote = row.advancePaid === true
      ? `\n\nAdvance ₹${row.advanceAmount || 11} was already paid online — balance ₹${Math.max(Number(row.remainingAmount) || 0, 0)} should have been collected at the salon.`
      : '';
    if (!window.confirm(`Mark this booking for ${row.customerName || 'the customer'} as service completed?${advanceNote}`)) {
      return;
    }
    try {
      await updateDoc(doc(db, 'bookings', row.id), {
        serviceStatus: 'completed',
        bookingStatus: 'completed',
        paymentStatus: 'received',
        paymentMode: 'offline',
        paymentReceivedAt: new Date().toISOString(),
        serviceCompletedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      alert('Booking marked as completed.');
      await fetchBookings();
    } catch (error) {
      alert('Failed to update booking. Please try again.');
    }
  };

  // Expired/unresolved past booking: salon records the customer never arrived.
  // Field set mirrors the mobile app's markBookingAsNoShow.
  const handleMarkExpiredNoShow = async (row) => {
    const advanceNote = row.advancePaid === true
      ? `\n\nThe ₹${row.advanceAmount || 11} advance already paid online is non-refundable and stays with the salon.`
      : '';
    if (!window.confirm(`Mark this booking for ${row.customerName || 'the customer'} as customer failed to arrive?${advanceNote}`)) {
      return;
    }
    try {
      await updateDoc(doc(db, 'bookings', row.id), {
        bookingStatus: 'no_show',
        serviceStatus: 'not_started',
        noShowMarkedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      alert('Booking marked as customer failed to arrive.');
      await fetchBookings();
    } catch (error) {
      alert('Failed to update booking. Please try again.');
    }
  };

  // Handle View Bill
  const handleViewBill = (booking) => {
    setSelectedBookingForBill(booking.id);
    setShowBillView(true);
  };

  // Close Bill View
  const handleCloseBillView = () => {
    setShowBillView(false);
    setSelectedBookingForBill(null);
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedBookings = filteredBookings.slice(startIndex, endIndex);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // First page button
    if (startPage > 1) {
      pages.push(
        <button key="first" onClick={() => handlePageChange(1)} className="pagination-btn">
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(<span key="ellipsis1" className="pagination-ellipsis">...</span>);
      }
    }

    // Page number buttons
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`pagination-btn ${currentPage === i ? 'active' : ''}`}
        >
          {i}
        </button>
      );
    }

    // Last page button
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<span key="ellipsis2" className="pagination-ellipsis">...</span>);
      }
      pages.push(
        <button key="last" onClick={() => handlePageChange(totalPages)} className="pagination-btn">
          {totalPages}
        </button>
      );
    }

    return (
      <div className="pagination-container">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="pagination-btn pagination-prev"
        >
          ← Previous
        </button>
        <div className="pagination-numbers">{pages}</div>
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="pagination-btn pagination-next"
        >
          Next →
        </button>
      </div>
    );
  };

  // Show loading if salon data is not yet loaded
  if (!salonData) {
    return (
      <div className="salon-bookings-page">
        <div className="loading-container">
          <p>Loading salon information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="salon-bookings-page">
        <div className="error-container">
          <p className="error-text">{error}</p>
          <button onClick={handleRefresh} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="salon-bookings-page">
      <div className="page-header">
        <div className="header-info">
          <h2>My Bookings</h2>
          <p>Total: {filteredBookings.length} bookings</p>
        </div>
        <button onClick={handleRefresh} className="refresh-btn" disabled={loading}>
          {loading ? 'Loading...' : '🔄 Refresh'}
        </button>
      </div>

      {/* Stats Summary */}
      <div className="bookings-stats">
        <div className="stat-item">
          <span className="stat-label">Total Bookings:</span>
          <span className="stat-value">{bookings.length}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Upcoming:</span>
          <span className="stat-value upcoming">
            {bookings.filter(b => {
              const status = b.bookingStatus.toLowerCase();
              const isUpcomingStatus = status === 'upcoming' || status === 'confirmed' || status === 'booked';
              
              if (!isUpcomingStatus) return false;
              
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const tomorrow = new Date(today);
              tomorrow.setDate(today.getDate() + 1);
              
              const bookingDate = new Date(b.bookingDate);
              const bookingDay = new Date(bookingDate);
              bookingDay.setHours(0, 0, 0, 0);
              
              return bookingDay >= tomorrow;
            }).length}
          </span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Completed:</span>
          <span className="stat-value completed">
            {bookings.filter(b => b.bookingStatus.toLowerCase() === 'completed').length}
          </span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Walk-in:</span>
          <span className="stat-value walkin">
            {bookings.filter(b => b.isWalkIn === true).length}
          </span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Cancelled:</span>
          <span className="stat-value cancelled">
            {bookings.filter(b => {
              const status = b.bookingStatus.toLowerCase();
              return status.includes('cancelled');
            }).length}
          </span>
        </div>
      </div>

      <div className="filters-container">
        <div className="filters">
          {filters.map((filter) => (
            <button
              key={filter.key}
              onClick={() => setActiveFilter(filter.key)}
              className={`filter-btn ${activeFilter === filter.key ? 'active' : ''}`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      <div className="table-section">
        {filteredBookings.length > 0 && (
          <div className="pagination-info">
            Showing {startIndex + 1} to {Math.min(endIndex, filteredBookings.length)} of {filteredBookings.length} bookings
          </div>
        )}
        
        <Table
          columns={getColumns()}
          data={paginatedBookings}
          loading={loading}
          emptyMessage="No bookings found for the selected filter"
        />
        
        {renderPagination()}
      </div>

      {/* Staff Assignment Modal */}
      {showStaffModal && selectedBooking && (
        <div className="modal-overlay" onClick={handleCloseStaffModal}>
          <div className="modal-content staff-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedBooking.staffId ? 'Reassign Staff' : 'Assign Staff'}</h3>
              <button className="modal-close-btn" onClick={handleCloseStaffModal}>✕</button>
            </div>
            
            <div className="modal-body">
              <div className="booking-info-modal">
                <p><strong>Customer:</strong> {selectedBooking.customerName}</p>
                <p><strong>Service:</strong> {selectedBooking.serviceName}</p>
                <p><strong>Date:</strong> {new Date(selectedBooking.bookingDate).toLocaleDateString()}</p>
                <p><strong>Time:</strong> {selectedBooking.bookingTime}</p>
                {selectedBooking.staffId && (
                  <p><strong>Current Staff:</strong> {staffMap[selectedBooking.staffId] || 'Unknown'}</p>
                )}
              </div>

              {staff.length === 0 ? (
                <p className="no-staff-message">No active staff members available. Please add staff in Staff Management first.</p>
              ) : (
                <div className="staff-list-modal">
                  <p className="staff-list-label">Select Staff Member:</p>
                  {staff.map((staffMember) => (
                    <button
                      key={staffMember.id}
                      onClick={() => handleAssignStaff(staffMember.id)}
                      className={`staff-item-btn ${selectedBooking.staffId === staffMember.id ? 'current-staff' : ''}`}
                      disabled={staffLoading}
                    >
                      <span className="staff-icon">👤</span>
                      <span className="staff-name">{staffMember.name}</span>
                      {selectedBooking.staffId === staffMember.id && (
                        <span className="current-badge">Current</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bill View Modal */}
      {showBillView && selectedBookingForBill && (
        <BillView 
          bookingId={selectedBookingForBill} 
          onClose={handleCloseBillView} 
        />
      )}
    </div>
  );
}

export default SalonBookings;

